import React from 'react';
import { ChevronDownIcon } from '@/components/shared';

interface ExpandableCardProps {
    title: string;
    description: string;
    icon: React.ReactNode;
    examples: string[];
    colorScheme?: 'green' | 'blue' | 'purple';
    specialFooter?: React.ReactNode;
    className?: string;
    isExpanded?: boolean;
    onToggle?: () => void;
}

export const ExpandableCard: React.FC<ExpandableCardProps> = ({
    title,
    description,
    icon,
    examples,
    colorScheme = 'green',
    specialFooter,
    className = '',
    isExpanded = false,
    onToggle
}) => {

    const colorClasses = {
        green: {
            iconBg: 'bg-gradient-to-br from-sage-green to-sage-green/80',
            bullet: 'text-sage-green',
            button: 'text-sage-green hover:text-sage-green/80',
            footerBg: 'bg-sage-green/5'
        },
        blue: {
            iconBg: 'bg-gradient-to-br from-bibi-blue to-bibi-blue/80',
            bullet: 'text-bibi-blue',
            button: 'text-bibi-blue hover:text-bibi-blue/80',
            footerBg: 'bg-bibi-blue/5'
        },
        purple: {
            iconBg: 'bg-gradient-to-br from-purple-600 to-purple-600/80',
            bullet: 'text-purple-600',
            button: 'text-purple-600 hover:text-purple-600/80',
            footerBg: 'bg-purple-600/5'
        }
    };

    const colors = colorClasses[colorScheme];

    return (
        <div
            className={`bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 flex flex-col cursor-pointer ${className}`}
            onClick={onToggle}
        >
            {/* Card Header */}
            <div className="p-8 text-center flex flex-col items-center flex-grow">
                <div className="flex-grow">
                    <div className={`w-16 h-16 ${colors.iconBg} rounded-full flex items-center justify-center mx-auto mb-6`}>
                        <div className="text-white">
                            {icon}
                        </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-2">{description}</p>
                </div>

                {/* Expand/Collapse Button */}
                <div className={`inline-flex items-center ${colors.button} font-medium transition-colors mt-3`}>
                    <span className="mr-2">{isExpanded ? 'Hide Examples' : 'Show Examples'}</span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
            </div>

            {/* Expandable Examples */}
            <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                } overflow-hidden`}>
                <div className="px-8 pb-8">
                    <div className="border-t border-gray-200 pt-6">
                        <ul className="space-y-3">
                            {examples.map((example, index) => (
                                <li key={index} className="flex items-baseline"> {/* MODIFIED: items-start to items-baseline */}
                                    <span className={`${colors.bullet} mr-3 flex-shrink-0`}>•</span> {/* MODIFIED: Removed mt-1 */}
                                    <span className="text-gray-600 text-sm leading-relaxed">{example}</span>
                                </li>
                            ))}
                        </ul>
                        {specialFooter && (
                            <div className={`mt-4 p-4 ${colors.footerBg} rounded-lg`}>
                                {specialFooter}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
